import i18next from 'i18next'
import {injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction, toJS} from 'mobx'
import {LoadingState} from '@/types'
import {TRangePickerContainerStore, TSelectContainerStore} from '~/code/components'
import {IPaymentsTransactionsFilterStore} from '~/code/pages/Overview/components/OveriviewPaymentsFilter'
import {Currencies} from '~/code/constants'
import {EcomStoreSelectStore, PosStoreSelectStore} from '../../store-and-terminals'
import {SelectContainerStore} from '../../select/SelectContainerStore'
import {RangePickerStore} from '../../date-picker'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {getDefaultAllStoresOption} from '../services/utills'
import {StoreOption} from '../models'

@injectable()
export class PaymentsTransactionsFilterStore implements IPaymentsTransactionsFilterStore {
    posStoreSelectStore: PosStoreSelectStore
    ecomStoreSelectStore: EcomStoreSelectStore
    paymentsTransactionsStatusStore: TSelectContainerStore
    paymentsTypeSelectStore: TSelectContainerStore
    currencySelectStore: TSelectContainerStore
    rangePickerStore: TRangePickerContainerStore
    selectedTransactionStore: StoreOption = null
    private isUpdatingFromStoreSelection = false
    constructor(
        posStoreSelectStore: PosStoreSelectStore,
        ecomStoreSelectStore: EcomStoreSelectStore,
        paymentsTypeSelectStore: SelectContainerStore,
        paymentsTransactionsStatusStore: SelectContainerStore,
        currencySelectStore: SelectContainerStore,
        rangePickerStore: RangePickerStore
    ) {
        this.posStoreSelectStore = posStoreSelectStore
        this.ecomStoreSelectStore = ecomStoreSelectStore
        this.paymentsTransactionsStatusStore = paymentsTransactionsStatusStore
        this.paymentsTypeSelectStore = paymentsTypeSelectStore
        this.currencySelectStore = currencySelectStore
        this.rangePickerStore = rangePickerStore
        makeObservable(this, {
            startDate: computed,
            endDate: computed,
            currency: computed,
            status: computed,
            selectedTransactionStore: observable,
            transactionStore: computed,
            statuses: computed,
            paymentsType: computed,
            posStoreValue: computed,
            ecomStoreValue: computed,
            setStatus: action.bound,
            setPaymentsType: action.bound,
            setSelectedTransactionStore: action.bound
        })

        this.currencySelectStore.setValue(Currencies.GBP)
        this.selectedTransactionStore = getDefaultAllStoresOption()
    }

    get startDate() {
        return this.rangePickerStore.startDate
    }

    get endDate() {
        return this.rangePickerStore.endDate.endOf('day')
    }

    get currency() {
        return this.currencySelectStore.value
    }

    get status() {
        return this.paymentsTransactionsStatusStore.value
    }

    get paymentsType() {
        return this.paymentsTypeSelectStore.value
    }

    get transactionStore() {
        return this.selectedTransactionStore
    }

    get statuses() {
        return this.paymentsTransactionsStatusStore.options
    }

    get posStoreValue() {
        return this.posStoreSelectStore.requestForServer
    }

    get ecomStoreValue() {
        return this.ecomStoreSelectStore.value
    }

    get paymentsTypes() {
        const options = []
        const hasEcomStores = (this.ecomStores?.length ?? 0) > 0
        const hasPosStores = (this.posStores?.length ?? 0) > 0

        // 'All Payments' is always visible
        options.push({
            label: i18next.t('PaymentsTransactionsFilter.allPayments'),
            value: 'all'
        })

        // 'POS' only visible if:
        // 1. User has pos_payments permission
        // 2. POS stores are available
        if (
            hasPermissions([PermissionsMap.pos_payments.read, PermissionsMap.pos_payments.full]) &&
            hasPosStores
        ) {
            options.push({
                label: i18next.t('PaymentsTransactionsFilter.posPayments'),
                value: 'pos'
            })
        }

        // 'ECOM' only visible if:
        // 1. User has online_payments permission
        // 2. ECOM stores are available
        if (
            hasPermissions([
                PermissionsMap.online_payments.read,
                PermissionsMap.online_payments.full
            ]) &&
            hasEcomStores
        ) {
            options.push({
                label: i18next.t('PaymentsTransactionsFilter.onlinePayments'),
                value: 'ecom'
            })
        }

        return options
    }

    private getStoresIfLoaded<T extends {value: any}>(store: {
        loadingState: LoadingState
        options?: T[]
    }) {
        const {loadingState} = store
        if (loadingState !== LoadingState.DONE && loadingState !== LoadingState.FAILED) {
            return undefined
        }
        return store.options?.filter((option) => option != null && option.value != null)
    }

    get ecomStores() {
        return this.getStoresIfLoaded(this.ecomStoreSelectStore)
    }

    get posStores() {
        return this.getStoresIfLoaded(this.posStoreSelectStore)
    }

    get storesOptions() {
        const options = []
        const currentPaymentsType = this.paymentsType

        // Only show POS stores if:
        // 1. User has pos_payments permission
        // 2. paymentsType is 'all' or 'pos'
        const showPosStores =
            this.posStores?.length &&
            hasPermissions([PermissionsMap.pos_payments.read, PermissionsMap.pos_payments.full]) &&
            (currentPaymentsType === 'all' || currentPaymentsType === 'pos')

        if (showPosStores) {
            options.push({
                label: i18next.t('PaymentsTransactionsFilter.posStores'),
                options: this.posStores.map((store) => ({
                    value: store.value,
                    label: store.labelPrimary,
                    secondaryLabel: store.labelSecondary,
                    type: 'pos'
                }))
            })
        }

        // Only show online stores if:
        // 1. User has online_payments permission
        // 2. paymentsType is 'all' or 'ecom'
        const showEcomStores =
            this.ecomStores?.length &&
            hasPermissions([
                PermissionsMap.online_payments.read,
                PermissionsMap.online_payments.full
            ]) &&
            (currentPaymentsType === 'all' || currentPaymentsType === 'ecom')

        if (showEcomStores) {
            options.push({
                label: i18next.t('PaymentsTransactionsFilter.onlineStores'),
                options: this.ecomStores.map((store) => ({
                    value: store.value,
                    label: store.label,
                    type: 'ecom'
                }))
            })
        }

        return [
            {
                label: '',
                options: [
                    {
                        value: 'all',
                        label: i18next.t('PaymentsTransactionsFilter.allStores'),
                        type: 'all'
                    }
                ]
            },
            ...options
        ]
    }

    public setSelectedTransactionStore = (store: StoreOption) => {
        // Set flag to indicate we're updating from store selection
        this.isUpdatingFromStoreSelection = true

        runInAction(() => {
            this.selectedTransactionStore = store ?? getDefaultAllStoresOption()

            // Only update paymentsType if a specific store type is selected (not 'all')
            // When 'all' stores is selected, keep the current paymentsType
            if (store.type !== 'all') {
                this.paymentsTypeSelectStore.setValue(store.type)
            }
        })

        if (store.type === 'ecom') {
            this.ecomStoreSelectStore.setValue(store.value)
            this.posStoreSelectStore.setValue(null)
        } else if (store.type === 'pos') {
            this.posStoreSelectStore.setValue(store.value)
            this.ecomStoreSelectStore.setValue(null)
        } else if (store.type === 'all') {
            // Clear both store filters but keep paymentsType unchanged
            this.ecomStoreSelectStore.setValue(null)
            this.posStoreSelectStore.setValue(null)
        }

        // Clear flag after all updates are done
        this.isUpdatingFromStoreSelection = false
    }

    public setStatus = (status) => {
        this.paymentsTransactionsStatusStore.setValue(status)
    }

    public setPaymentsType = (type) => {
        this.paymentsTypeSelectStore.setValue(type)

        // If this is NOT from store selection, reset transactionStore to "All Stores"
        if (!this.isUpdatingFromStoreSelection) {
            runInAction(() => {
                this.selectedTransactionStore = getDefaultAllStoresOption()
                this.ecomStoreSelectStore.setValue(null)
                this.posStoreSelectStore.setValue(null)
            })
        }
    }
}
